import React, { useEffect, useState, useCallback } from "react";
import {
  Plus,
  CheckCircle,
  PencilSimple,
  Trash,
  Calendar,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", start_date: "", end_date: "", is_active: false };

function SessionDialog({ open, onOpenChange, initial, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setError("");
    }
  }, [open, initial]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(cleanPayload(form));
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  const isEdit = Boolean(initial?.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit session" : "Add academic session"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Rename or adjust the date range. Active status is changed via the Activate action."
              : "Create a new academic year. Marking it active will deactivate any existing active session."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input
              required
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. 2025-26"
              className="mt-1.5"
              data-testid="session-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input
                type="date"
                value={form.start_date || ""}
                onChange={update("start_date")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>End date</Label>
              <Input
                type="date"
                value={form.end_date || ""}
                onChange={update("end_date")}
                className="mt-1.5"
              />
            </div>
          </div>
          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={update("is_active")}
                data-testid="session-active-checkbox"
                className="h-4 w-4 rounded border-slate-300"
              />
              Set as active session
            </label>
          )}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-blue-900 hover:bg-blue-800"
              data-testid="session-submit-btn"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create session"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school/sessions");
      setSessions(data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (payload) => {
    const { data } = await api.post("/school/sessions", payload);
    setSessions((s) => [data, ...s]);
    toast.success(`Session "${data.name}" created`);
    if (data.is_active) {
      // Server auto-deactivates others; refresh to reflect
      load();
    }
  };

  const edit = async (payload) => {
    const { data } = await api.patch(`/school/sessions/${editing.id}`, payload);
    setSessions((s) => s.map((x) => (x.id === data.id ? data : x)));
    toast.success("Session updated");
  };

  const activate = async (s) => {
    try {
      await api.post(`/school/sessions/${s.id}/activate`);
      load();
      toast.success(`"${s.name}" is now the active session`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/school/sessions/${confirmDelete.id}`);
      setSessions((s) => s.filter((x) => x.id !== confirmDelete.id));
      toast.success(`Session "${confirmDelete.name}" deleted`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6" data-testid="sessions-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Academic
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Sessions <span className="text-slate-400 font-normal">({sessions.length})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage academic years. One session is always marked as <em>active</em> — every fee
            payment will reference it.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          data-testid="add-session-btn"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> Add session
        </Button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden" data-testid="sessions-table-wrap">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="p-10 text-center" data-testid="sessions-empty">
            <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              No sessions yet. Click <strong>Add session</strong> to create your first academic
              year.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Name</th>
                <th className="text-left font-semibold px-4 py-3">Period</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sessions.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-slate-50 transition-colors"
                  data-testid={`session-row-${s.id}`}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    {s.start_date || "—"} <span className="text-slate-400">→</span>{" "}
                    {s.end_date || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {s.is_active ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700"
                        data-testid={`session-active-badge-${s.id}`}
                      >
                        <CheckCircle size={12} weight="fill" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-200 text-slate-700">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">
                    {new Date(s.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {!s.is_active && (
                        <button
                          onClick={() => activate(s)}
                          data-testid={`activate-session-${s.id}`}
                          className="px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditing(s);
                          setDialogOpen(true);
                        }}
                        data-testid={`edit-session-${s.id}`}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                        title="Edit"
                      >
                        <PencilSimple size={15} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(s)}
                        data-testid={`delete-session-${s.id}`}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Delete"
                        disabled={s.is_active}
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SessionDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        initial={editing}
        onSubmit={editing ? edit : create}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.name}</strong>. Any future fee payments
              referencing it will fail. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-session"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
